import * as babylon from "@babylonjs/core";

import * as schema from "@/shared/state/visualization/schema/v1/three-d";

import { BabylonState } from "../../state";

/**
 * Solves the 'distant origin' problem by centering the scene around
 * the point the camera is looking at.
 *
 * Distant origin arises from the float64 JS numbers being converted to
 * float32 precision in the gpu shaders.
 *
 * 64-bit floats can accurately represent about 1e15 decimal places,
 * allowing them to model 1e9km down to millimeter precision.
 * (This is ~6.7AU, eg the solar system out to Jupiter)
 *
 * 32-bit floats can only represent about 1e6-1e7 decimal places,
 * so they can only model a region less than 10km across to millimeter precision.
 *
 * For an example of what it looks like when looking at objects
 * far from the origin, see here (photosensitivity warning!):
 * https://www.reddit.com/r/Unity3D/comments/148ctmb/dont_forget_floating_origin_if_youre_working_with/
 */
export function applyRenderOrigin(state: BabylonState) {
  const origin = state.sceneConfig.camera?.focusPoint ?? babylon.Vector3.Zero();

  // Position all meshes based on the render origin.
  for (const entity of Object.values(state.entities)) {
    if (!entity.position) {
      continue;
    }
    if (entity.mesh) {
      entity.mesh.mesh.position =
        entity.position.current.vector.subtract(origin);
    }
    if (entity.path?.mesh) {
      entity.path.mesh.position = entity.position.mean.subtract(origin);
    }
    if (entity.vectorField?.mesh) {
      entity.vectorField.mesh.position = entity.position.mean.subtract(origin);
    }
    if (entity.vectorField?.currentMesh) {
      entity.vectorField.currentMesh.position =
        entity.position.current.vector.subtract(origin);
    }
  }

  // Place the grid relative to the camera's focus.
  const grid = state.sceneConfig.grid;
  if (grid && grid.spec.scale && grid.mesh) {
    // Data-space ground anchor derived inline each frame from current
    // scene state. Orbit follows the tracked entity's first cached Y;
    // third-person picks the first positioned entity. The user-supplied
    // `spec.verticalOffset` overrides both. Computing per-frame (rather
    // than snapshot-caching) lets the grid track the data as components
    // are reconfigured — a default-zero position spec replaced with
    // real topic data now repositions the grid as soon as the cache
    // reloads, with no toggle needed.
    const y = grid.spec.verticalOffset ?? deriveGroundY(state);
    if (y !== grid.verticalOffset) {
      // TODO: broadcast this computed value to the react state
      // to be able to show the default value in the panel settings.
      grid.verticalOffset = y;
    }
    const gridDataPosition = new babylon.Vector3(
      origin.x - (origin.x % grid.spec.scale),
      y,
      origin.z - (origin.z % grid.spec.scale),
    );
    grid.mesh.position = gridDataPosition.subtract(origin);
  }
}

/**
 * Resolves the grid's data-space Y anchor from current scene state.
 *
 * Orbit prefers the followed entity's first cached Y so the grid sits
 * under the tracked object. Third-person — and any orbit case where the
 * tracked entity hasn't loaded a cache yet — falls back to the first
 * positioned entity in iteration order. Returns `0` when no entity has
 * a populated cache (e.g. the data-load race window on initial
 * render); `applyRenderOrigin` will pick up the real anchor on the
 * next frame once a cache lands.
 */
function deriveGroundY(state: BabylonState): number {
  const camera = state.sceneConfig.camera;
  if (camera?.spec.type === schema.CAMERA_TYPE.orbit) {
    const tracked = state.entities[camera.spec.target.id];
    const firstY = tracked?.position?.cache.allPoints[0]?.y;
    if (firstY !== undefined) {
      return firstY;
    }
  }
  for (const entity of Object.values(state.entities)) {
    const firstY = entity.position?.cache.allPoints[0]?.y;
    if (firstY !== undefined) {
      return firstY;
    }
  }
  return 0;
}

export const RENDER_INTERVAL_MS = 1000 / 60;
